import { GameRules } from "./gameRules";
import { TetrominoType, RotateType } from "../const/const";

export interface GameStats {
    score: number;
    level: number;
    lines: number;
    time: string;
    goal: number;
    tetrises: number;
    tspins: number;
    combos: number;
    tpm: number;
    lpm: number;
}

export interface LockResult {
    scoreAdded: number;
    actionName: string | null;
    isBackToBack: boolean;
    garbage: number;
    clearedLines: number;
    combo: number;
    level: number;
}

export interface GarbageAttackInput {
    clearedLineCount: number;
    isTSpin: boolean;
    isTSpinMini: boolean;
    isBackToBackBonus: boolean;
    comboCount: number;
    isPerfectClear?: boolean;
    actionName?: string | null;
}

export interface LockContext {
    isImmobile?: boolean;
    isPerfectClear?: boolean;
}

export class ScoreSystem {
    private level: number = 1;
    private score: number = 0;
    private clearedLines: number = 0;
    private nextLevelRequireClearedLines: number = 5;
    private comboCount: number = -1;
    private isBackToBackChain: boolean = false;

    // New stats
    private startTime: number = 0;
    private totalLinesCleared: number = 0;
    private tetrisCount: number = 0;
    private tSpinCount: number = 0;
    private maxCombo: number = 0;
    private totalPiecesLocked: number = 0;

    constructor() {
        this.clear();
    }

    clear() {
        this.level = 1;
        this.score = 0;
        this.clearedLines = 0;
        this.nextLevelRequireClearedLines = 5;
        this.comboCount = -1;
        this.isBackToBackChain = false;

        // Reset new stats
        this.startTime = Date.now();
        this.totalLinesCleared = 0;
        this.tetrisCount = 0;
        this.tSpinCount = 0;
        this.maxCombo = 0;
        this.totalPiecesLocked = 0;
    }

    start() {
        this.startTime = Date.now();
    }

    getLevel() {
        return this.level;
    }

    getScore() {
        return this.score;
    }

    getClearedLines() {
        return this.clearedLines;
    }

    getNextLevelRequireClearedLines() {
        return this.nextLevelRequireClearedLines;
    }

    getCombo() {
        return this.comboCount;
    }

    static calculateGarbageAttack(input: GarbageAttackInput): number {
        let garbage = 0;

        if (input.isTSpinMini) {
            if (input.clearedLineCount === 2) garbage = 1;
        } else if (input.isTSpin) {
            if (input.clearedLineCount === 1) garbage = 2;
            else if (input.clearedLineCount === 2) garbage = 4;
            else if (input.clearedLineCount === 3) garbage = 6;
        } else {
            if (input.clearedLineCount === 2) garbage = 1;
            else if (input.clearedLineCount === 3) garbage = 2;
            else if (input.clearedLineCount === 4) garbage = 4;
        }

        if (input.isBackToBackBonus) garbage += 1;

        if (input.comboCount >= 2) {
            if (input.comboCount < 4) garbage += 1;
            else if (input.comboCount < 6) garbage += 2;
            else if (input.comboCount < 8) garbage += 3;
            else if (input.comboCount < 10) garbage += 4;
            else garbage += 5;
        }

        if (input.isPerfectClear) {
            garbage += 10;
        }

        if (input.actionName) {
            garbage += GameRules.getActionGarbageBonus(input.actionName);
        }

        return garbage;
    }

    setAuthoritativeState(
        score: number,
        level: number,
        lines: number,
        extras?: { tetrises?: number; tspins?: number; combos?: number }
    ) {
        const normalizedScore = Number.isFinite(score) ? Math.max(0, Math.floor(score)) : 0;
        const normalizedLevel = Number.isFinite(level) ? Math.max(1, Math.floor(level)) : 1;
        const normalizedLines = Number.isFinite(lines) ? Math.max(0, Math.floor(lines)) : 0;

        this.score = normalizedScore;
        this.level = normalizedLevel;
        this.clearedLines = normalizedLines;
        this.totalLinesCleared = normalizedLines;

        let nextRequired = 5;
        for (let lv = 2; lv <= this.level; lv += 1) {
            nextRequired += lv * 5;
        }
        this.nextLevelRequireClearedLines = nextRequired;

        this.comboCount = -1;
        this.isBackToBackChain = false;

        if (extras) {
            if (Number.isFinite(extras.tetrises)) {
                this.tetrisCount = Math.max(0, Math.floor(extras.tetrises as number));
            }
            if (Number.isFinite(extras.tspins)) {
                this.tSpinCount = Math.max(0, Math.floor(extras.tspins as number));
            }
            if (Number.isFinite(extras.combos)) {
                this.maxCombo = Math.max(0, Math.floor(extras.combos as number));
            }
        } else {
            this.maxCombo = Math.max(0, this.maxCombo);
        }
    }

    /**
     * Calculate score and stats when a piece locks.
     */
    onLock(
        clearedLineCount: number,
        tetrominoType: TetrominoType,
        droppedRotateType: RotateType,
        lockedRotateType: RotateType,
        movement: string,
        kickDataIndex: number,
        dropCounter: { softDrop: number, hardDrop: number, autoDrop: number },
        tSpinCornerOccupiedCount: { pointSide: number, flatSide: number },
        lockContext?: LockContext
    ): LockResult {
        this.totalPiecesLocked++;
        this.totalLinesCleared += clearedLineCount;

        const { pointSide, flatSide } = tSpinCornerOccupiedCount;
        const isRotateLock = droppedRotateType != lockedRotateType && movement == 'rotate';
        const isImmobile = lockContext?.isImmobile === true;
        const isPerfectClear = lockContext?.isPerfectClear === true;

        // Is T-Spin
        const isTSpin =
            tetrominoType == TetrominoType.T &&
            isRotateLock &&
            pointSide + flatSide > 2;

        if (isTSpin) this.tSpinCount++;
        if (clearedLineCount === 4) this.tetrisCount++;

        // Is T-Spin mini
        const isTSpinMini =
            isTSpin &&
            pointSide < 2 &&
            kickDataIndex < 3;

        // Optional non-T spin labeling. Kept score/garbage-neutral by default.
        const isNonTSpin =
            !isTSpin &&
            tetrominoType !== TetrominoType.T &&
            tetrominoType !== TetrominoType.GARBAGE &&
            isRotateLock &&
            isImmobile;

        let isBackToBackBonus = false;
        if (this.isBackToBackChain) {
            this.isBackToBackChain = Boolean(isTSpin || clearedLineCount == 4 || !clearedLineCount);
            isBackToBackBonus = this.isBackToBackChain && clearedLineCount > 0;
        } else {
            this.isBackToBackChain = Boolean((isTSpin && clearedLineCount) || clearedLineCount == 4);
        }

        const clearedLineAction = clearedLineCount
            ? ['Single', 'Double', 'Triple', 'Tetris'][clearedLineCount - 1]
            : null;

        const actionNameArray: string[] = [];
        if (isTSpin) {
            actionNameArray.push('T-Spin');
            if (isTSpinMini) actionNameArray.push('Mini');
        } else if (isNonTSpin) {
            actionNameArray.push(`${tetrominoType}-Spin`);
        }
        if (clearedLineAction) actionNameArray.push(clearedLineAction);

        let actionName: string | null = null;
        let scoreBaseAction: string | null = null;
        if (actionNameArray.length) {
            actionName = actionNameArray.join(' ');
            if (isTSpin) {
                scoreBaseAction = actionName;
            } else if (clearedLineAction) {
                // Non-T spin labels keep base line-clear score by default.
                scoreBaseAction = clearedLineAction;
            }
        }

        const scoreBase = scoreBaseAction ? GameRules.getBaseScore(scoreBaseAction) : 0;
        let scoreAdded = 0;
        const actionScoreBase = scoreBase + (actionName ? GameRules.getActionScoreBonus(actionName) : 0);
        if (actionScoreBase) {
            const score = actionScoreBase * (isBackToBackBonus ? 1.5 : 1) * this.level;
            scoreAdded += score;
        }

        // Combo
        if (clearedLineCount) this.comboCount++;
        else this.comboCount = -1;

        if (this.comboCount > this.maxCombo) {
            this.maxCombo = this.comboCount;
        }

        if (this.comboCount > 0) {
            const comboScore = 50 * this.comboCount * this.level;
            scoreAdded += comboScore;
        }

        // Drop Score
        scoreAdded += dropCounter.softDrop;
        scoreAdded += dropCounter.hardDrop * 2;

        this.score += scoreAdded;

        // Calculate Level by actual cleared lines (Guideline: line-clear accumulation)
        if (clearedLineCount > 0) {
            this.addLineCount(clearedLineCount);
        }

        const garbage = ScoreSystem.calculateGarbageAttack({
            clearedLineCount,
            isTSpin,
            isTSpinMini,
            isBackToBackBonus,
            comboCount: this.comboCount,
            isPerfectClear,
            actionName,
        });

        // Return result
        return {
            scoreAdded,
            actionName: actionName ? `${isBackToBackBonus ? 'Back to Back ' : ''}${actionName}` : null,
            isBackToBack: isBackToBackBonus,
            garbage,
            clearedLines: clearedLineCount,
            combo: this.comboCount,
            level: this.level
        };
    }

    private addLineCount(count: number) {
        this.clearedLines += count;
        if (this.clearedLines >= this.nextLevelRequireClearedLines) {
            while (true) {
                this.level++;
                this.nextLevelRequireClearedLines += this.level * 5;
                if (this.clearedLines < this.nextLevelRequireClearedLines) break;
            }
        }
    }

    getAutoDropDelay() {
        return Math.pow((0.8 - ((this.level - 1) * 0.007)), (this.level - 1)) * 1000;
    }

    getStats(gameTime: number): GameStats {
        // Format Time: MM:SS.ms
        const minutes = Math.floor(gameTime / 60000);
        const seconds = Math.floor((gameTime % 60000) / 1000);
        const ms = Math.floor((gameTime % 1000) / 10); // Display 2 digits (centiseconds)

        const timeStr = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}.${ms.toString().padStart(2, '0')}`;

        const minutesFloat = gameTime / 60000;
        const tpm = minutesFloat > 0 ? Math.floor(this.totalPiecesLocked / minutesFloat) : 0;
        const lpm = minutesFloat > 0 ? Math.floor(this.totalLinesCleared / minutesFloat) : 0;

        return {
            score: this.score,
            level: this.level,
            lines: this.totalLinesCleared,
            time: timeStr,
            goal: this.nextLevelRequireClearedLines - this.clearedLines,
            tetrises: this.tetrisCount,
            tspins: this.tSpinCount,
            combos: this.maxCombo,
            tpm: tpm,
            lpm: lpm
        };
    }
}
